import { WorkspaceIntegration, WorkspaceIntegrationStatuses } from '@prisma/client';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@zond/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { openConnection } from '../../integrations.queries';
import { IntegrationSettingsDialog } from '../integration-settings-dialog';

interface AppCardProps {
  name: string;
  description: string;
  logoUrl: string;
  isConnected: boolean;
  isAvailableToPublic: boolean;
}

export function AppCard({ name, description, logoUrl, isConnected, isAvailableToPublic }: AppCardProps) {
  const { t } = useTranslation('integrations');
  const [isConnecting, setIsConnecting] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const queryClient = useQueryClient();

  const providerKey = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  const localizedDescription = t(`providers.${providerKey}`, { defaultValue: description });

  const handleConnect = async () => {
    if (isConnected) {
      setSettingsOpen(true);
      return;
    }

    if (!isConnecting) {
      setIsConnecting(true);
      try {
        await openConnection(name);
        await queryClient.invalidateQueries({ queryKey: ['integrations'] });
        const data = (await queryClient.fetchQuery({ queryKey: ['integrations'] })) as WorkspaceIntegration[];

        const isNowConnected = data?.some(
          (integration) =>
            integration.integrationType.toLowerCase() === name.toLowerCase() &&
            integration.status === WorkspaceIntegrationStatuses.Enable
        );

        if (isNowConnected) {
          toast.success(t('appCard.toast.connected', { name }));
        }
        setIsConnecting(false);
      } catch (error) {
        toast.error(t('appCard.toast.connectFailed', { name }));
        setIsConnecting(false);
      }
    }
  };

  const getButtonText = () => {
    if (isConnecting) return t('appCard.button.connecting');
    if (isConnected) return t('common:actions.edit');
    if (isAvailableToPublic) return t('appCard.button.connect');
    return t('appCard.button.comingSoon');
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <>
      <li className="rounded-lg border p-4 hover:shadow-md transition-shadow">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex size-10 items-center justify-center rounded-lg bg-muted p-2">
              {imageError ? (
                <div className="text-sm font-medium">{name.slice(0, 2).toUpperCase()}</div>
              ) : (
                <img
                  src={logoUrl}
                  alt={t('appCard.logoAlt', { name })}
                  className="size-full object-contain"
                  onError={handleImageError}
                />
              )}
            </div>
          </div>
          <Button
            variant={isConnected ? 'default' : 'outline'}
            size="sm"
            onClick={handleConnect}
            disabled={isConnecting || !isAvailableToPublic}
          >
            {getButtonText()}
          </Button>
        </div>
        <div>
          <h2 className="mb-1 font-semibold">{name}</h2>
          <p className="line-clamp-2 text-gray-500">{localizedDescription}</p>
        </div>
      </li>

      <IntegrationSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        integrationName={name}
        logoUrl={logoUrl}
      />
    </>
  );
}
